// Route card (contracts.md:50). GET /v1/apps/:id/route -> RouteRecommendation is the
// FROZEN shape (packages/shared/src/model/route.ts), but RouteRecommendation carries no
// fingerprint field and no other listed Slice-2 endpoint exposes
// `{hasPayments,hasAuth,hasPii,stack,buildTarget}` - yet the route card's mandated
// "What we detected" panel (slice-2-onboarding.md §8) needs exactly that. RESOLVED (a
// contract gap, not guessed against apps/api internals): this lane's `RouteCardResponse`
// composes two ALREADY-FROZEN shared schemas (`RouteRecommendation`, `FingerprintSummary`)
// into one envelope for this one screen. Flagged for the tech-lead/apps/api lane to
// confirm the real endpoint returns this shape (or a fingerprint sub-resource is added).
import { z } from "zod";
import {
  AppId,
  Cloud,
  ConnectionMode,
  FingerprintSummary,
  NonEmptyString,
  RouteRecommendation,
} from "@nightcrew/shared";
import type { Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

export const RouteCardResponse = z.object({
  recommendation: RouteRecommendation,
  fingerprint: FingerprintSummary,
});
export type RouteCardResponse = z.infer<typeof RouteCardResponse>;

export const getRouteCard = (appId: AppId): Promise<Result<RouteCardResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/route`, RouteCardResponse);

export const ConfirmRouteResponse = z.object({ routeConfirmedAt: z.iso.datetime() });
export type ConfirmRouteResponse = z.infer<typeof ConfirmRouteResponse>;

export const confirmRoute = (appId: AppId): Promise<Result<ConfirmRouteResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/route/confirm`, ConfirmRouteResponse, {});

export const SetCloudRequest = z.object({ cloud: Cloud, region: z.string().trim().min(1) });
export type SetCloudRequest = z.infer<typeof SetCloudRequest>;

// RECONCILED at Wave-3 integration: POST /v1/apps/:id/cloud returns the connection result
// (apps/api/src/cloud/cloud.schema.ts CloudConnectResponse), NOT an App record.
export const CloudConnectResponse = z.object({
  connectionId: NonEmptyString,
  mode: ConnectionMode,
  cloud: z.string(),
  region: z.string(),
});
export type CloudConnectResponse = z.infer<typeof CloudConnectResponse>;

export const setAppCloud = (
  appId: AppId,
  request: SetCloudRequest,
): Promise<Result<CloudConnectResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/cloud`, CloudConnectResponse, request);
